import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CompanyProvider } from "@/lib/company";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import { Home, OpsDash, FuelDash } from "@/pages/Dashboards";
import Approvals from "@/pages/Approvals";
import { Trips, TripDetail } from "@/pages/Trips";
import Spend from "@/pages/Spend";
import Payouts from "@/pages/Payouts";
import Purchasing from "@/pages/Purchasing";
import Stores from "@/pages/Stores";
import JobCards from "@/pages/JobCards";
import Tyres from "@/pages/Tyres";
import Accidents from "@/pages/Accidents";
import Yard from "@/pages/Yard";
import Salaries from "@/pages/Salaries";
import Quotes from "@/pages/Quotes";
import FuelDepot from "@/pages/FuelDepot";
import Registers from "@/pages/Registers";
import { Roles, Audit } from "@/pages/Admin";
import Accounts from "@/pages/Accounts";
import Ledger from "@/pages/Ledger";
import Journals from "@/pages/Journals";
import NewJournal from "@/pages/NewJournal";
import Reports from "@/pages/Reports";
import Setup from "@/pages/Setup";
import Users from "@/pages/Users";
import Statements from "@/pages/Statements";

function Protected({ children, need }) {
  const { user } = useAuth();
  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (need && !need.some((p) => (user.perms || []).includes(p))) return <Navigate to="/" replace />;
  return <CompanyProvider><Layout>{children}</Layout></CompanyProvider>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== false) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<Protected><Home /></Protected>} />
            <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
            <Route path="/ops" element={<Protected need={["dash.ops"]}><OpsDash /></Protected>} />
            <Route path="/fuel" element={<Protected need={["dash.fuel"]}><FuelDash /></Protected>} />
            <Route path="/trips" element={<Protected need={["trips.view"]}><Trips /></Protected>} />
            <Route path="/trips/:id" element={<Protected need={["trips.view"]}><TripDetail /></Protected>} />
            <Route path="/spend" element={<Protected need={["expense.request", "float.request", "float.pay", "float.approve", "float.topup"]}><Spend /></Protected>} />
            <Route path="/salaries" element={<Protected need={["payroll.view"]}><Salaries /></Protected>} />
            <Route path="/yard" element={<Protected need={["yard.log", "dash.ops", "dash.owner", "dash.workshop", "audit.view"]}><Yard /></Protected>} />
            <Route path="/accidents" element={<Protected need={["accidents.report", "accidents.manage", "dash.owner", "books.view", "audit.view"]}><Accidents /></Protected>} />
            <Route path="/tyres" element={<Protected need={["tyres.manage", "jobcard.open", "jobcard.approve", "stores.issue", "registers.view", "books.view", "dash.owner", "dash.workshop"]}><Tyres /></Protected>} />
            <Route path="/jobcards" element={<Protected need={["jobcard.open", "jobcard.approve", "jobcard.close", "tyres.manage", "stores.issue", "stores.manage", "books.view", "dash.owner", "dash.workshop"]}><JobCards /></Protected>} />
            <Route path="/stores" element={<Protected need={["stores.receive", "stores.issue", "stores.count", "stores.manage", "lpo.request", "lpo.create", "jobcard.approve", "jobcard.open", "books.view", "dash.owner", "dash.workshop", "approve.final"]}><Stores /></Protected>} />
            <Route path="/purchasing" element={<Protected need={["lpo.request", "lpo.create", "lpo.approve.l1", "lpo.approve.l2", "lpo.approve.l3", "lpo.send", "suppliers.manage", "stores.receive", "supplier_invoice.capture", "payment.initiate", "books.view", "dash.owner", "jobcard.approve"]}><Purchasing /></Protected>} />
            <Route path="/payouts" element={<Protected need={["float.pay", "float.approve", "books.view", "dash.owner"]}><Payouts /></Protected>} />
            <Route path="/quotes" element={<Protected need={["quote.request", "jobcard.approve", "float.approve"]}><Quotes /></Protected>} />
            <Route path="/depot" element={<Protected need={["fuel.issue", "fuel.dip", "fuel.receive", "fuel.outside.request"]}><FuelDepot /></Protected>} />
            <Route path="/registers" element={<Protected need={["registers.view"]}><Registers /></Protected>} />
            <Route path="/books" element={<Protected need={["books.view"]}><Dashboard /></Protected>} />
            <Route path="/accounts" element={<Protected need={["books.view"]}><Accounts /></Protected>} />
            <Route path="/ledger/:aid" element={<Protected need={["books.view"]}><Ledger /></Protected>} />
            <Route path="/journals" element={<Protected need={["books.view"]}><Journals /></Protected>} />
            <Route path="/journals/new" element={<Protected need={["books.post"]}><NewJournal /></Protected>} />
            <Route path="/reports" element={<Protected need={["books.view"]}><Reports /></Protected>} />
            <Route path="/statements" element={<Protected need={["books.view"]}><Statements /></Protected>} />
            <Route path="/setup" element={<Protected need={["books.view"]}><Setup /></Protected>} />
            <Route path="/users" element={<Protected need={["users.manage"]}><Users /></Protected>} />
            <Route path="/roles" element={<Protected need={["roles.manage"]}><Roles /></Protected>} />
            <Route path="/audit" element={<Protected need={["audit.view"]}><Audit /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}
